import { produce } from "immer";
import { omit } from "lodash";
import moment from "moment";
import { from, Observable } from "rxjs";
import { Option } from "space-monad";
import { API } from "../../services/http";
import { Pageable } from "../../services/pageable";
import { FetchableError, FetchableSuccess } from "../common/fetchable/helpers";
import { processErrorFromPromise } from "../errors/utils";
import { triggerEvent } from "../eventstore/trigger-event";
import { successNotificationFromPromise } from "../notifications/utils";
import { UsersDTO } from "./models/dto";
import { TwoFAProviders } from "./models/internal";

const processUser = (body: any): UsersDTO.Responses.GetSingle.T =>
  produce<any, UsersDTO.Responses.GetSingle.T>(body, (body: any) => {
    body.dateOfBirth = body.dateOfBirth
      ? moment.utc(body.dateOfBirth).toDate()
      : body.dateOfBirth;

    body.nationality = body.nationality
      ? body.nationality.toUpperCase()
      : body.nationality;

    const levels = body.levels;
    delete body.levels;

    if (!Array.isArray(levels)) {
      body.level = undefined;
      body.believer = false;
    } else {
      body.believer = levels.includes("BELIEVER");
      const filtered = levels.filter((x) => x !== "BELIEVER" && x !== "NONE");
      body.level = filtered.length < 1 ? undefined : filtered[0];
    }
  });

export namespace UsersService {
  const api = new API("auth");
  export function getUserDetails(
    userId: string
  ): Observable<
    FetchableError | FetchableSuccess<UsersDTO.Responses.GetSingle.T>
  > {
    return from(
      api
        .get(`v1/users/${userId}`)
        .then<FetchableSuccess<UsersDTO.Responses.GetSingle.T>>((response) => ({
          status: "ready",
          data: processUser(response.body),
        }))
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function editUserDetails(
    id: string,
    userDetails: Omit<UsersDTO.Requests.UserDetails.T, "emailAddress">
  ): Observable<
    FetchableError | FetchableSuccess<UsersDTO.Responses.GetSingle.T>
  > {
    return from(
      api
        .put(`v1/users/${id}`)
        .send(userDetails)
        .then(successNotificationFromPromise("user-successfully-updated"))
        .then<FetchableSuccess<UsersDTO.Responses.GetSingle.T>>((response) => ({
          status: "ready",
          data: processUser(response.body),
        }))
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getAllUsers(
    pagination: Pageable.T<keyof UsersDTO.Responses.GetSingle.T> = {}
  ): Observable<
    FetchableError | FetchableSuccess<UsersDTO.Responses.GetAll.T>
  > {
    return from(
      api
        .post(`admin/users/search`)
        .query(Pageable.getQueryParam(omit(pagination, "filters")))
        .send({
          ...Option(pagination.filters)
            .map((f) =>
              f.reduce(
                (acc, curr) => ({ ...acc, [curr.field]: curr.value }),
                {}
              )
            )
            .getOrElse({}),
          excludeDelegates: true,
          excludeMerchants: true,
        })
        .then<FetchableSuccess<UsersDTO.Responses.GetAll.T>>((response) => ({
          status: "ready",
          data: produce(response.body, (b: any) => {
            b.content = b.content.map(processUser);
          }) as any,
        }))
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getUser(
    userId: UsersDTO.Responses.GetSingle.T["userReferenceId"]
  ): Promise<UsersDTO.Responses.GetSingle.T> {
    return api
      .get(`v1/users/${userId}`)
      .then<UsersDTO.Responses.GetSingle.T>((response) =>
        processUser(response.body)
      )
      .catch<UsersDTO.Responses.GetSingle.T>(processErrorFromPromise);
  }

  export function getMerchant(
    userId: UsersDTO.Responses.GetSingle.T["userReferenceId"]
  ): Promise<UsersDTO.Responses.GetSingle.T> {
    return api
      .get(`v1/merchants/${userId}`)
      .then<UsersDTO.Responses.GetSingle.T>((response) => response.body)
      .catch<UsersDTO.Responses.GetSingle.T>(processErrorFromPromise);
  }

  export function unlockUser(
    userId: UsersDTO.Responses.GetSingle.T["userReferenceId"]
  ): Promise<void> {
    return api
      .delete(`admin/users/${userId}/lock`)
      .then<void>(() => {})
      .then(successNotificationFromPromise("user-successfully-unlocked"))
      .catch<void>(processErrorFromPromise);
  }

  export function lockUser(
    userId: UsersDTO.Responses.GetSingle.T["userReferenceId"]
  ): Promise<void> {
    return api
      .put(`admin/users/${userId}/lock`)
      .then<void>(() => {})
      .then(successNotificationFromPromise("user-successfully-locked"))
      .catch<void>(processErrorFromPromise);
  }

  export function resendVerificationEmail(
    emailAddress: UsersDTO.Responses.GetSingle.T["emailAddress"]
  ): Promise<void> {
    return api
      .post(`admin/users/verify/resend`)
      .send({ emailAddress })
      .then<void>(() => {})
      .then(successNotificationFromPromise("resent-verification-e-mail"))
      .catch<void>(processErrorFromPromise);
  }

  export function delete2FA(
    provider: UsersDTO.Responses.GetSingle.T["currentProvider"],
    userId: UsersDTO.Responses.GetSingle.T["userReferenceId"]
  ): Promise<void> {
    return api
      .delete(`admin/users/${userId}/2fa`)
      .query({ provider })
      .then<void>(() => {})
      .then(successNotificationFromPromise("2fa-method-has-been-reset"))
      .catch<void>(processErrorFromPromise);
  }

  export function update2FA(
    userId: UsersDTO.Responses.GetSingle.T["userReferenceId"],
    provider: typeof TwoFAProviders.T = "NONE"
  ): Promise<void> {
    return api
      .put(`admin/users/${userId}/login/2fa`)
      .query({ "2faProvider": provider })
      .then<void>(() => {})
      .then(successNotificationFromPromise("2fa-provider-updated"))
      .catch<void>(processErrorFromPromise);
  }

  export function getAllMerchants(
    pagination: Pageable.T<keyof UsersDTO.Responses.GetSingle.T> & {
      options?: { excludeSubMerchants?: boolean };
    } = {}
  ): Promise<UsersDTO.Responses.GetAllMerchants.T> {
    const { options, ...actualPagination } = pagination;
    return api
      .get(`v1/merchants`)
      .query({
        ...Pageable.getQueryParam(actualPagination),
        ...Option(actualPagination)
          .map((x) => x.filters)
          .map((x) => x.find((y) => y.field === "companyDetails"))
          .map((x) => x.value)
          .map<object>((company) => ({ company }))
          .getOrElse({}),
        showEnabledOnly: false,
        excludeSubMerchants: !!options?.excludeSubMerchants,
      })
      .then<UsersDTO.Responses.GetAllMerchants.T>(
        (response) =>
          produce(response.body, (b: any) => {
            b.content = b.content.map(processUser);
          }) as any
      )
      .catch<UsersDTO.Responses.GetAllMerchants.T>(processErrorFromPromise);
  }

  export function GetSingle(
    userId: UsersDTO.Responses.GetSingle.T["userReferenceId"]
  ): Promise<UsersDTO.Responses.GetSingle.T> {
    return api
      .get(`v1/merchants/${userId}`)
      .then<UsersDTO.Responses.GetSingle.T>((response) => response.body)
      .catch<UsersDTO.Responses.GetSingle.T>(processErrorFromPromise);
  }

  export function getAllDelegateUsers(
    merchantId: string,
    pagination: Pageable.T<keyof UsersDTO.Responses.GetSingle.T> = {}
  ): Observable<
    FetchableError | FetchableSuccess<UsersDTO.Responses.GetAll.T>
  > {
    return from(
      api
        .get(`v1/merchants/${merchantId}/delegates`)
        .query({
          ...Pageable.getQueryParam(omit(pagination, "filters")),
          showEnabledOnly: false,
        })
        .then<FetchableSuccess<UsersDTO.Responses.GetAll.T>>((response) => ({
          status: "ready",
          data: produce(response.body, (b: any) => {
            b.content = b.content.map(processUser);
          }) as any,
        }))
        .catch<FetchableError>(processErrorFromPromise)
        .catch<FetchableError>((err) => ({
          status: "error",
          err,
        }))
    );
  }

  export function getAllSubMerchants(
    merchantId: string,
    pagination: Pageable.T<keyof UsersDTO.Responses.GetSingle.T> = {}
  ): Promise<UsersDTO.Responses.GetAll.T> {
    return api
      .get(`v1/merchants/${merchantId}/sub`)
      .query({
        ...Pageable.getQueryParam(omit(pagination, "filters")),
        showEnabledOnly: false,
      })
      .then(
        (response) =>
          produce(response.body, (b: any) => {
            b.content = b.content.map(processUser);
          }) as any
      )
      .catch(processErrorFromPromise);
  }

  export function removeSubMerchant(
    merchantId: string,
    submerchantId: string
  ): Promise<void> {
    return api
      .delete(`v1/merchants/${merchantId}/sub/${submerchantId}`)
      .then<void>(() => {})
      .then(successNotificationFromPromise("removed-submerchant"))
      .catch<void>(processErrorFromPromise);
  }

  export function createSubmerchant(
    parentmerchantId: string,
    data: UsersDTO.Requests.Merchant.Create.T
  ): Promise<UsersDTO.Responses.Merchant.Create.T> {
    return api
      .post(`v1/merchants/${parentmerchantId}/sub`)
      .send({ ...data, username: data.emailAddress })
      .then<UsersDTO.Responses.Merchant.Create.T>((res) => res.body)
      .then(successNotificationFromPromise("added-submerchant"))
      .catch<UsersDTO.Responses.Merchant.Create.T>(processErrorFromPromise);
  }

  export function createMerchant(
    data: UsersDTO.Requests.Merchant.Create.T
  ): Promise<UsersDTO.Responses.Merchant.Create.T> {
    return api
      .post(`v1/merchants/`)
      .send({ ...data, username: data.emailAddress })
      .then<UsersDTO.Responses.Merchant.Create.T>((res) => {
        return res.body;
      })
      .then(triggerEvent("CREATE_MERCHANT_SUCCESS"))
      .catch<UsersDTO.Responses.Merchant.Create.T>(processErrorFromPromise);
  }

  export function removeDelegateUser(
    merchantId: string,
    delegateId: string
  ): Promise<void> {
    return api
      .delete(`v1/merchants/${merchantId}/delegates/${delegateId}`)
      .then<void>(() => {})
      .then(successNotificationFromPromise("removed-delegate"))
      .catch<void>(processErrorFromPromise);
  }
  export function addDelegateUser(
    merchantId: string,
    delegateId: string
  ): Promise<void> {
    return api
      .put(`v1/merchants/${merchantId}/delegates/${delegateId}`)
      .then<void>(() => {})
      .then(successNotificationFromPromise("added-delegate"))
      .catch<void>(processErrorFromPromise);
  }
}
